import type React from 'react'
import { useEffect, useState, useRef, useCallback } from 'react'
import { Edit2, Loader2, LogOut } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

import MainTopBar from '@/components/common/bar/MainTopBar'
import ChatMessageInput from '@/components/chat/ChatMessageInput'
import ChatMessageBubble from '@/components/chat/ChatMessageBubble'
import ChatExitModal from '@/components/chat/ChatExitModal'
import ChatRateExitModal from '@/components/chat/ChatRateExitModal'
import ChatEditTitleModal from '@/components/chat/ChatEditTitleModal'
import ToastMessage from '@/components/common/toast/ToastMessage'
import {
  getNoneStreamingContinueConsultationApi,
  getConsultationChatMessageListApi,
  getConsultationDetailApi,
  editConsultationTitle,
  getRecommendationResponses
} from '@/api/chatApi'
import { EXIT_REASONS } from '@/utils/exitReasons'
import { TOAST_MESSAGE } from '@/utils/toastMessages'
import { useChatStore } from '@/store/chatStore'
import type { ChatMessage, IChatRoomState, IToastState, FinishConsultationBody } from '@/api/model/chatType'
import { useGetConsultationList, useGetMyStepsByLatestBlock } from '@/hooks/queries/chat'
import { useFinishConsultation, useFeedbackConsultation } from '@/hooks/queries/chat/mutation'
import { SSEViewer } from '@/components/userSSE/SSEViewer'
import { incrementMessageCount, resetRiskDetectionCount } from '@/utils/riskStateManager'
import { useAuthStore } from '@/store/authStore'
import { getUserIdFromToken } from '@/utils/jwt'
import { getCheckTokenApi } from '@/api/authApi'
import { useUserStore } from '@/store/userStore'
import { getUserInfoApi } from '@/api/userApi'
import { SESSION_STORAGE_KEYS, safeSessionStorage } from '@/constants/storageKeys'
import { logger } from '@/utils/logger'

const RECOMMENDATION_RESPONSE_INTERVAL = 10000 // 10초

const formatChatDateKey = (value?: string | Date) => {
  const resolveDate = (target?: string | Date) => {
    if (target instanceof Date) return target
    if (!target) return new Date()

    const direct = new Date(target)
    if (!Number.isNaN(direct.getTime())) return direct

    if (typeof target === 'string') {
      const normalized = new Date(target.replace(' ', 'T'))
      if (!Number.isNaN(normalized.getTime())) return normalized
    }

    return new Date()
  }

  return resolveDate(value)
    .toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })
    .replace(/\./g, '')
    .replace(/\s/g, '')
}

// 채팅 메시지 날짜별 그룹화 함수
const groupMessagesByDate = (messages: any[]): IChatRoomState[] => {
  const grouped: IChatRoomState[] = []

  messages.forEach(apiMsg => {
    // 빈 메시지 필터링
    if (apiMsg.role_name === 'user' && apiMsg.content.trim().length === 0) return

    const messageDate = formatChatDateKey(apiMsg.created_at)

    const chatMessage: ChatMessage = {
      id: apiMsg.id.toString(),
      text: apiMsg.content,
      sender: apiMsg.role_name === 'user' ? 'user' : 'ai',
      timestamp: new Date(apiMsg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }

    const existingGroup = grouped.find(group => group.date === messageDate)
    if (existingGroup) {
      existingGroup.messagesList.push(chatMessage)
    } else {
      grouped.push({ date: messageDate, messagesList: [chatMessage] })
    }
  })

  // 각 그룹 내 메시지를 시간순으로 정렬
  grouped.forEach(group => {
    group.messagesList.sort((a, b) => {
      const parseTime = (date: string, time: string) => {
        if (!time) return 0
        const [ampm, hm] = time.split(' ')
        const [hRaw, m] = hm.split(':').map(Number)
        let h = hRaw
        if (ampm === '오후' && h < 12) h += 12
        if (ampm === '오전' && h === 12) h = 0
        const dateStr = `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`
        return new Date(`${dateStr}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`).getTime()
      }
      return parseTime(group.date, a.timestamp) - parseTime(group.date, b.timestamp)
    })
  })

  return grouped
}

function NonStreamingChatRoomContent() {
  const navigate = useNavigate()
  const location = useLocation()
  const { refetch: refetchConsultationList } = useGetConsultationList()
  const { refetch: refetchGetMyStepsByLatestBlock } = useGetMyStepsByLatestBlock()
  const finishConsultationMutation = useFinishConsultation()
  const feedbackMutation = useFeedbackConsultation()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesTopRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const scrollContainerRef = useRef<HTMLElement>(null)
  const recommendationContainerRef = useRef<HTMLDivElement>(null)

  // 상태 관리
  const [messages, setMessages] = useState<IChatRoomState[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [consultationId, setConsultationId] = useState<number | null>(null)
  const [consultationDetail, setConsultationDetail] = useState<any>(null)

  // SSE 연결 상태 관리
  const [isSSEConnected, setIsSSEConnected] = useState(false)
  const sseConnectionRef = useRef<{ connect: () => void; disconnect: () => void } | null>(null)

  // 모달 상태
  const [showExitModal, setShowExitModal] = useState(false)
  const [showEditTitleModal, setShowEditTitleModal] = useState(false)
  const [showRatingModal, setShowRatingModal] = useState(false)
  const [showToast, setShowToast] = useState<IToastState | null>(null)

  // 폼 상태
  const [selectedReason, setSelectedReason] = useState('')
  // const [customReason, setCustomReason] = useState('')
  const [newTitle, setNewTitle] = useState('')
  const [rating, setRating] = useState(0)
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false)
  const [lastError, setLastError] = useState<string | null>(null)
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null)
  const [isConsultationComplete, setIsConsultationComplete] = useState(false)

  // 무한스크롤 관련 상태
  const [currentPage, setCurrentPage] = useState(0)
  const [hasMoreMessages, setHasMoreMessages] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)
  const [lastFetchTime, setLastFetchTime] = useState(0)

  // 채팅 스토어
  const { setRecentChatPage } = useChatStore()
  const { userInfo, setUserInfo } = useUserStore()

  // 사용자 비활동 감지를 위한 ref
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null)
  const recommendedResponsesRef = useRef<string[]>([])
  const apiCallIdRef = useRef<number>(0)

  // 추천 응답 상태
  const [recommendedResponses, setRecommendedResponses] = useState<string[]>([])

  // SSE 연결 상태를 확인하고 필요시 재연결을 시도합니다.
  const ensureSSEConnection = useCallback(async (): Promise<boolean> => {
    // 이미 연결된 경우
    if (isSSEConnected) {
      return true
    }

    console.log('[Chat] SSE not connected, attempting to reconnect...')

    // 수동으로 재연결 시도
    if (sseConnectionRef.current) {
      sseConnectionRef.current.connect()
    }

    // 최대 5초 대기 (500ms 간격으로 10번 체크)
    const maxAttempts = 10
    const checkInterval = 500

    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, checkInterval))

      if (isSSEConnected) {
        console.log('[Chat] SSE reconnected successfully')
        return true
      }
    }

    console.error('[Chat] SSE reconnection failed after 5 seconds')
    return false
  }, [isSSEConnected])

  // 사용자 비활동 타이머 리셋 함수
  const resetInactivityTimer = (targetConsultationId: number) => {
    // 진행 중인 상담만 추천 질문 나오도록
    if (consultationDetail && consultationDetail.status !== 'active') {
      return
    }

    // 추천 응답이 이미 표시되어 있으면 타이머를 다시 시작하지 않음 (ref 사용)
    if (recommendedResponsesRef.current.length > 0) {
      return
    }

    // 기존 타이머가 있으면 클리어
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
    }

    // API call ID 증가
    const currentCallId = ++apiCallIdRef.current

    // 새로운 10초 타이머 설정
    inactivityTimerRef.current = setTimeout(async () => {
      // API 호출 전 ID 체크
      if (currentCallId !== apiCallIdRef.current) {
        return
      }

      try {
        const res = await getRecommendationResponses(targetConsultationId)

        // API 응답 후 ID 체크
        if (currentCallId !== apiCallIdRef.current) {
          setRecommendedResponses([])
          return
        }

        if (res) {
          const newResponses = [res.recommended_response_1, res.recommended_response_2, res.recommended_response_3]
          setRecommendedResponses(newResponses) // 최대 3개까지만 표시
          recommendedResponsesRef.current = newResponses // ref도 업데이트
          setShouldScrollToBottom(true) // 스크롤을 맨 아래로

          // React Native로 추천 응답 전송 제거 - 웹뷰에서 직접 표시
        }
      } catch (error) {
        setRecommendedResponses([])
        console.error('추천 응답 가져오기 실패:', error)
      }
    }, RECOMMENDATION_RESPONSE_INTERVAL)
  }

  // 로컬 메시지 추가 함수
  const addLocalMessage = useCallback((message: ChatMessage, createdAt?: string | Date) => {
    const messageDate = formatChatDateKey(createdAt)

    setMessages(prev => {
      const existingGroup = prev.find(group => group.date === messageDate)

      const updatedGroups = existingGroup
        ? prev.map(group =>
            group.date === messageDate ? { ...group, messagesList: [...group.messagesList, message] } : group
          )
        : [...prev, { date: messageDate, messagesList: [message] }]

      return [...updatedGroups].sort((a, b) => parseInt(a.date, 10) - parseInt(b.date, 10))
    })
  }, [])

  // 메시지 전송 핸들러 (기존 채팅에서만 사용)
  const handleSendMessage = async (input: string) => {
    if (input.trim() === '' || isLoading) {
      return
    }

    // SSE 연결 상태 확인 및 재연결 (백그라운드에서 조용히 처리)
    const isSSEReady = await ensureSSEConnection()

    if (!isSSEReady) {
      // SSE 재연결 실패해도 메시지는 정상적으로 전송
      // 단지 Risk 이벤트를 놓칠 수 있음을 내부적으로만 로그
      console.warn('[Chat] Proceeding without SSE connection - Risk events may be missed')
    }

    // 기존 타이머가 있으면 클리어 (AI 응답 pending 중 추천응답 방지)
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
      apiCallIdRef.current += 1 // API call ID 증가하여 진행 중인 API 무효화
    }

    // risk 상태 메시지 카운트 증가
    incrementMessageCount()

    // 추천 응답 숨기기
    setRecommendedResponses([])
    recommendedResponsesRef.current = [] // ref도 클리어

    // React Native로 추천 응답 전송 제거 - 웹뷰에서 직접 표시

    setIsLoading(true)

    // React Native로 로딩 상태 전송 제거 - 웹뷰에서 직접 표시

    // 사용자 메시지 추가
    const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: input,
      sender: 'user',
      timestamp: currentTime
    }
    addLocalMessage(userMessage)

    // 응답 대기 중인 AI 메시지 추가 (빈 메시지로 ... 애니메이션 표시)
    const streamingId = `streaming-${Date.now()}`
    setStreamingMessageId(streamingId)
    const waitingMessage: ChatMessage = {
      id: streamingId,
      text: '',
      sender: 'ai',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    addLocalMessage(waitingMessage)

    setShouldScrollToBottom(true)
    setInputValue('')
    handleFocusMessageInput()

    try {
      // 기존 채팅 계속
      const currentConsultationId = Number(location.pathname.split('/')[2])
      const response = await getNoneStreamingContinueConsultationApi(currentConsultationId, input)

      // 스트리밍 메시지를 실제 응답으로 교체
      if (response?.content) {
        setMessages(prev =>
          prev.map(group => ({
            ...group,
            messagesList: group.messagesList.map(msg =>
              msg.id === streamingId
                ? {
                    ...msg,
                    id: response.id.toString(),
                    text: response.content,
                    timestamp: new Date(response.created_at).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  }
                : msg
            )
          }))
        )
        setStreamingMessageId(null)
        setLastError(null) // 성공 시 에러 상태 초기화
        resetInactivityTimer(currentConsultationId)

        // is_complete 체크하여 상담 완료 처리
        if (response.is_complete === true) {
          setIsConsultationComplete(true)
          // consultationDetail도 업데이트하여 입력창 비활성화
          setConsultationDetail((prev: any) => ({
            ...prev,
            status: 'completed'
          }))
        }

        // 메시지 전송 완료 후 SSE 재연결 확인 (백그라운드)
        // 연결이 끊겼을 수 있으므로 다시 확인
        if (!isSSEConnected) {
          console.log('[Chat] Checking SSE connection after message completion...')
          setTimeout(() => {
            ensureSSEConnection().then(connected => {
              if (connected) {
                console.log('[Chat] SSE reconnected after message completion')
              }
            })
          }, 1000) // 1초 후 재연결 시도
        }
      }
    } catch (error) {
      console.error('메시지 전송 실패:', error)
      setLastError(input) // 마지막 실패한 메시지 저장

      // 스트리밍 메시지를 에러 메시지로 교체
      setMessages(prev =>
        prev.map(group => ({
          ...group,
          messagesList: group.messagesList.map(msg =>
            msg.id === streamingId
              ? {
                  ...msg,
                  text: '메시지 전송에 실패했습니다. 다시 시도해주세요.'
                }
              : msg
          )
        }))
      )
      setStreamingMessageId(null)
    } finally {
      setIsLoading(false)

      // React Native로 로딩 상태 전송 제거 - 웹뷰에서 직접 표시

      // 스크롤을 맨 아래로
      setShouldScrollToBottom(true)
    }
  }

  // 키보드 이벤트 핸들러
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage(inputValue)
    }
  }

  // 채팅 종료 핸들러
  const handleConfirmExit = async (skip: boolean) => {
    try {
      if (consultationId) {
        // skip이 true(아니오)일 때만 reason 필요
        // const reason = skip
        //   ? consultationDetail?.type === '자유상담'
        //     ? ''
        //     : selectedReason === 'other'
        //       ? customReason
        //       : (EXIT_REASONS.find(r => r.value === selectedReason)?.label ?? '')
        //   : ''

        // 기타여도 텍스트 필드 X
        const reason = skip
          ? consultationDetail?.type === '자유상담'
            ? ''
            : (EXIT_REASONS.find(r => r.value === selectedReason)?.label ?? '')
          : ''

        const body: FinishConsultationBody = {
          skip,
          reason
        }

        try {
          // React Query mutation 사용
          await finishConsultationMutation.mutateAsync({
            consultation_id: consultationId,
            body
          })
          refetchGetMyStepsByLatestBlock()
        } catch (err) {
          console.error(err)
        }

        // mutation의 onSuccess에서 자동으로 쿼리 무효화 처리됨
        setShowExitModal(false)
        navigate('/chat')
      }
    } catch (error) {
      console.error('채팅 종료 실패:', error)
      // 에러 토스트 메시지 표시 가능
      setShowToast('exitFailed')
    }
  }

  // 평가 제출 핸들러
  const handleConfirmFeedback = async () => {
    try {
      if (consultationId) {
        // mutation 사용으로 변경
        await feedbackMutation.mutateAsync({
          consultation_id: consultationId,
          rating,
          skip: false
        })
      }
      setShowRatingModal(false)
      setRating(0) // 평가 초기화
      navigate('/chat')
    } catch (error) {
      console.error('평가 제출 실패:', error)
      setShowToast('feedbackFailed')
    }
  }

  // 평가 없이 넘어가기 핸들러
  const handleSkipFeedback = async () => {
    try {
      if (consultationId) {
        await feedbackMutation.mutateAsync({
          consultation_id: consultationId,
          rating: 0,
          skip: true
        })
      }
      setShowRatingModal(false)
      setRating(0)
      navigate('/chat')
    } catch (error) {
      console.error('평가 건너뛰기 실패:', error)
      navigate('/chat') // 에러 발생해도 페이지는 이동
    }
  }

  // 제목 수정 핸들러
  const handleSaveTitle = async () => {
    try {
      if (consultationId && newTitle.trim() !== consultationDetail?.title) {
        await editConsultationTitle(consultationId, newTitle.trim())

        // 상담 목록 갱신
        refetchConsultationList()
        // 상세 정보 다시 로드
        loadConsultationDetail()
      }
      setShowEditTitleModal(false)
    } catch (error) {
      console.error('제목 수정 실패:', error)
    }
  }

  // 상담 상세 정보 로드
  const loadConsultationDetail = async () => {
    const currentConsultationId = Number(location.pathname.split('/')[2])
    if (currentConsultationId) {
      try {
        const detail = await getConsultationDetailApi(currentConsultationId)
        setConsultationDetail(detail)
        setConsultationId(currentConsultationId)
        return detail // detail 반환
      } catch (error) {
        console.error('상담 상세 정보 로드 실패:', error)
        return null
      }
    }
    return null
  }

  // 채팅 메시지 로드 (무한스크롤 지원)
  const loadChatMessages = useCallback(
    async (page = 0, isLoadMore = false) => {
      const currentConsultationId = Number(location.pathname.split('/')[2])
      if (currentConsultationId) {
        try {
          if (isLoadMore) {
            setIsLoadingMore(true)
          }

          const response = await getConsultationChatMessageListApi(currentConsultationId, {
            offset: page * 10,
            limit: 10
          })

          if (response.messages && response.messages.length > 0) {
            // 스크롤 위치 보존을 위한 처리
            const container = scrollContainerRef.current || messagesEndRef.current?.parentElement
            let prevScrollHeight = 0
            let prevScrollTop = 0

            if (isLoadMore && container) {
              prevScrollHeight = container.scrollHeight
              prevScrollTop = container.scrollTop
            }

            // 서버에서 받은 메시지는 최신순이므로 reverse로 오래된 것부터 정렬
            const sortedMessages = response.messages.reverse()
            const newGroupedMessages = groupMessagesByDate(sortedMessages)

            // 각 그룹 내 메시지를 시간순으로 정렬
            newGroupedMessages.forEach(group => {
              group.messagesList.sort((a, b) => {
                const parseTime = (date: string, time: string) => {
                  if (!time) return 0
                  const [ampm, hm] = time.split(' ')
                  const [hRaw, m] = hm.split(':').map(Number)
                  let h = hRaw
                  if (ampm === '오후' && h < 12) h += 12
                  if (ampm === '오전' && h === 12) h = 0
                  const dateStr = `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`
                  return new Date(`${dateStr}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`).getTime()
                }
                return parseTime(group.date, a.timestamp) - parseTime(group.date, b.timestamp)
              })
            })

            if (isLoadMore) {
              // 기존 메시지와 새 메시지 합치기
              setMessages(prevMessages => {
                // 날짜별 그룹을 관리하는 Map
                const dateGroupsMap = new Map<string, IChatRoomState>()

                // 먼저 기존 메시지(화면에 있던 것)를 그대로 추가 - 순서 유지
                prevMessages.forEach(prevGroup => {
                  dateGroupsMap.set(prevGroup.date, {
                    date: prevGroup.date,
                    messagesList: [...prevGroup.messagesList] // 기존 순서 그대로 유지
                  })
                })

                // 새로운 메시지(서버)를 앞에 추가 - 중복 제거
                newGroupedMessages.forEach(newGroup => {
                  const existingGroup = dateGroupsMap.get(newGroup.date)

                  if (existingGroup) {
                    // 같은 날짜 그룹이 있으면 중복 제거하며 앞에 추가
                    // 기존 메시지의 id로 중복 체크 Set 생성
                    const existingMessagesSet = new Set(existingGroup.messagesList.map(m => m.id))

                    // 새 메시지 중 중복되지 않은 것만 필터링
                    const uniqueNewMessages = newGroup.messagesList.filter(msg => {
                      return !existingMessagesSet.has(msg.id)
                    })

                    // 새 메시지를 기존 메시지 앞에 추가 (오래된 메시지가 앞으로)
                    existingGroup.messagesList = [...uniqueNewMessages, ...existingGroup.messagesList]
                  } else {
                    // 새로운 날짜 그룹이면 그대로 추가
                    dateGroupsMap.set(newGroup.date, { ...newGroup })
                  }
                })

                // Map을 배열로 변환하고 날짜순 정렬
                const allGroupedMessages = Array.from(dateGroupsMap.values()).sort(
                  (a, b) => parseInt(a.date) - parseInt(b.date)
                )

                return allGroupedMessages
              })

              // 스크롤 위치 복원 - 보편적인 무한스크롤 방식
              requestAnimationFrame(() => {
                if (container && prevScrollHeight > 0) {
                  const newScrollHeight = container.scrollHeight
                  const heightDiff = newScrollHeight - prevScrollHeight
                  // 이전 스크롤 위치를 정확히 유지 (추가 오프셋 없음)
                  const newScrollTop = prevScrollTop + heightDiff

                  // 즉시 스크롤 위치 설정 (애니메이션 없음)
                  container.scrollTop = newScrollTop
                }
              })
            } else {
              // 첫 로드
              setMessages(prevMessages => {
                // 서버에서 메시지가 없고 현재 메시지가 있으면 (RoomCheckPage에서 온 경우)
                if (newGroupedMessages.length === 0 && prevMessages.length > 0) {
                  return prevMessages
                }

                // 서버에서 메시지를 받은 경우
                return newGroupedMessages
              })

              setShouldScrollToBottom(true)
              setTimeout(() => {
                setIsInitialLoadComplete(true)
              }, 500)
            }

            // 더 많은 메시지가 있는지 확인
            setHasMoreMessages(response.messages.length === 10)
            setCurrentPage(page)

            // AI 메시지 존재 여부 반환 (첫 로드 시에만)
            if (!isLoadMore) {
              return response.messages.some(msg => msg.role_name !== 'user')
            }
          } else {
            setHasMoreMessages(false)
            // 메시지가 없을 때도 false 반환
            if (!isLoadMore) {
              return false
            }
          }
        } catch (error) {
          console.error('채팅 메시지 로드 실패:', error)
          setHasMoreMessages(false)
          // 에러 시에도 false 반환
          if (!isLoadMore) {
            return false
          }
        } finally {
          if (isLoadMore) {
            setIsLoadingMore(false)
          }
        }
      }
    },
    [location.pathname]
  )

  // 더 많은 메시지 로드
  const loadMoreMessages = useCallback(() => {
    if (!hasMoreMessages || isLoadingMore) return

    const now = Date.now()
    if (now - lastFetchTime < 300) return // 300ms 쿨다운으로 단축

    setLastFetchTime(now)
    loadChatMessages(currentPage + 1, true)
  }, [hasMoreMessages, isLoadingMore, currentPage, lastFetchTime, loadChatMessages])

  // 추천 응답 클릭 핸들러
  const handleRecommendationClick = useCallback(
    (recommendation: string) => {
      // 이미 로딩 중이면 무시 (중복 방지)
      if (isLoading) {
        console.log('[Chat] 이미 전송 중 - 추천응답 클릭 무시')
        return
      }

      // 추천 응답 즉시 숨기기
      setRecommendedResponses([])
      recommendedResponsesRef.current = [] // ref도 클리어

      // React Native로 추천 응답 전송 제거 - 웹뷰에서 직접 표시

      // 입력창 비우기 (사용자가 입력한 텍스트가 있을 수 있음)
      if (inputValue.trim() !== '') {
        setInputValue('')
      }

      // 타이머 클리어
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
        inactivityTimerRef.current = null
        apiCallIdRef.current += 1
      }

      // 메시지 전송
      handleSendMessage(recommendation)
    },
    [inputValue, handleSendMessage, isLoading]
  )

  // 주기적으로 SSE 연결 상태 확인 및 재연결
  useEffect(() => {
    // 상담이 활성 상태이고 consultationId가 있을 때만 실행
    if (!consultationId || consultationDetail?.status !== 'active') {
      return
    }

    // 30초마다 SSE 연결 상태 확인
    const checkInterval = setInterval(() => {
      if (!isSSEConnected && sseConnectionRef.current) {
        console.log('[Chat] Periodic check: SSE disconnected, attempting reconnection...')
        // 수동으로 재연결 시도
        sseConnectionRef.current.connect()

        // 재연결 확인
        setTimeout(() => {
          if (isSSEConnected) {
            console.log('[Chat] Periodic check: SSE reconnected successfully')
          } else {
            console.log('[Chat] Periodic check: SSE still disconnected, will retry later')
          }
        }, 2000) // 2초 후 상태 확인
      }
    }, 30000) // 30초마다 확인

    return () => {
      clearInterval(checkInterval)
    }
  }, [isSSEConnected, consultationId, consultationDetail?.status])

  // 브릿지 핸들러를 ref로 관리하여 stale closure 방지
  const handleSendMessageRef = useRef(handleSendMessage)
  const handleRecommendationClickRef = useRef(handleRecommendationClick)
  const isLoadingRef = useRef(isLoading)

  // ref 업데이트
  useEffect(() => {
    handleSendMessageRef.current = handleSendMessage
    handleRecommendationClickRef.current = handleRecommendationClick
    isLoadingRef.current = isLoading
  }, [handleSendMessage, handleRecommendationClick, isLoading])

  // React Native 브릿지 리스너 제거 - 웹뷰에서 직접 처리

  // 상담 상태 전송 제거 - 웹뷰에서 직접 처리

  // 초기 데이터 로드
  useEffect(() => {
    const currentConsultationId = Number(location.pathname.split('/')[2])

    if (currentConsultationId && !location.pathname.includes('/chat-prepare')) {
      // 페이지 진입 시 추천응답 초기화
      setRecommendedResponses([])
      recommendedResponsesRef.current = []

      // React Native 추천응답 전송 제거 - 웹뷰에서 직접 표시

      // async 처리를 위한 함수
      const loadInitialData = async () => {
        // 먼저 상담 상세 정보 로드
        const detail = await loadConsultationDetail()

        // RoomCheckPage에서 전달받은 초기 응답이 있는 경우
        if (location.state?.initialResponse) {
          const { initialResponse } = location.state

          // 사용자 메시지가 있다면 먼저 추가
          if (location.state.message) {
            const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            const userMessage: ChatMessage = {
              id: (Date.now() - 1).toString(),
              text: location.state.message,
              sender: 'user',
              timestamp: currentTime
            }
            addLocalMessage(userMessage)
          }

          // AI 응답 추가
          if (initialResponse.content) {
            const aiMessage: ChatMessage = {
              id: initialResponse.id.toString(),
              text: initialResponse.content,
              sender: 'ai',
              timestamp: new Date(initialResponse.created_at).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })
            }
            addLocalMessage(aiMessage, initialResponse.created_at)
            setShouldScrollToBottom(true)

            // 초기 AI 응답이 있고 active 상태면 타이머 시작
            if (detail && detail.status === 'active') {
              resetInactivityTimer(currentConsultationId)
            }
          }

          setConsultationId(currentConsultationId)

          // 새로운 상담 시작 시 risk 카운트 초기화
          resetRiskDetectionCount()

          // initialResponse가 있어도 서버에서 전체 메시지 로드
          await loadChatMessages()
        } else {
          // 기존 채팅인 경우 메시지 로드
          const hasAiMessage = await loadChatMessages()

          // AI 메시지가 있고 active 상태면 타이머 시작 (채팅방 진입 시)
          if (detail && detail.status === 'active' && hasAiMessage) {
            resetInactivityTimer(currentConsultationId)
          }
        }
      }

      loadInitialData()

      // 최근 채팅 페이지 설정
      setRecentChatPage(currentConsultationId)
    }

    // 컴포넌트 언마운트 시 정리
    return () => {
      // 타이머 정리
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
        inactivityTimerRef.current = null
      }

      // 추천응답 정리
      setRecommendedResponses([])
      recommendedResponsesRef.current = []
    }
  }, [location.pathname]) // location 대신 location.pathname 사용

  // 스크롤 처리
  useEffect(() => {
    if (shouldScrollToBottom) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
        setShouldScrollToBottom(false)
      }, 100)
    }
  }, [messages, shouldScrollToBottom])

  // 메시지 전송 후 input focus
  const handleFocusMessageInput = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.focus()
    }
  }, [])

  // AI 응답 재시도
  const handleRetryAiResponse = useCallback(
    (type?: string) => {
      const accessToken = useAuthStore.getState().getAccessToken()
      const prevFunction = () => {
        if (type === 'message') {
          // 마지막 user 메시지 제거 (불변성 유지)
          setMessages(prev => {
            // 모든 그룹을 역순으로 확인하여 마지막 user 메시지 찾기
            for (let i = prev.length - 1; i >= 0; i--) {
              const group = prev[i]

              // 현재 그룹에서 마지막 user 메시지 찾기
              for (let j = group.messagesList.length - 1; j >= 0; j--) {
                if (group.messagesList[j].sender === 'user') {
                  // user 메시지를 찾았으므로 제거
                  const newMessages = [...prev]
                  const newGroup = { ...newMessages[i] }
                  newGroup.messagesList = [...newGroup.messagesList]
                  newGroup.messagesList.splice(j, 1)

                  // 그룹이 비어있으면 그룹 자체 제거
                  if (newGroup.messagesList.length === 0) {
                    newMessages.splice(i, 1)
                  } else {
                    newMessages[i] = newGroup
                  }

                  return newMessages
                }
              }
            }

            return prev
          })
        }
        if (lastError !== null) {
          // 에러 메시지 제거
          setMessages(prev => {
            return prev
              .map(group => ({
                ...group,
                messagesList: group.messagesList.filter(
                  msg => !(msg.sender === 'ai' && msg.text === '메시지 전송에 실패했습니다. 다시 시도해주세요.')
                )
              }))
              .filter(group => group.messagesList.length > 0)
          })

          // 다시 시도
          handleSendMessage(lastError)
          setLastError(null)
        }
      }
      const asyncFunction = async () => {
        if (accessToken !== null && accessToken !== undefined) {
          const decodedUserId = getUserIdFromToken(accessToken)
          // isTokenValid 제거 - Axios 인터셉터가 자동으로 처리
          if (decodedUserId) {
            if (
              userInfo.user_id === 0 ||
              userInfo.user_id !== decodedUserId ||
              userInfo.user_id === undefined ||
              userInfo.user_id === null
            ) {
              const newUserInfo = (await getUserInfoApi(decodedUserId)).data
              setUserInfo(newUserInfo)
              if (newUserInfo.is_agree === false) {
                navigate('/auth/login')
                // navigate(`/auth/tos?provider=${userInfo.join_path}&user_id=${userInfo.user_id}`)
              }
            } else {
              if (userInfo.is_agree === false) {
                navigate('/auth/login')
                // navigate(`/auth/tos?provider=${userInfo.join_path}&user_id=${userInfo.user_id}`)
              }
            }
          } else {
            const res = await getCheckTokenApi()
            try {
              if (res.code === '401' && res.message === '토큰이 만료되었습니다') {
                // Axios 인터셉터가 자동으로 토큰 갱신 처리
                try {
                  return prevFunction()
                } catch (error) {
                  logger.error('재시도 중 오류 발생:', error)
                  useAuthStore.getState().clearAuth()
                  navigate('/auth/login')
                }
              } else if (res.code === '200') {
                const decodedUserId = getUserIdFromToken(accessToken)
                if (
                  decodedUserId &&
                  (userInfo.user_id === 0 ||
                    userInfo.user_id !== decodedUserId ||
                    userInfo.user_id === undefined ||
                    userInfo.user_id === null)
                ) {
                  const newUserInfo = (await getUserInfoApi(decodedUserId)).data
                  setUserInfo(newUserInfo)
                  if (newUserInfo.is_agree === false) {
                    navigate('/auth/login')
                    // navigate(`/auth/tos?provider=${userInfo.join_path}&user_id=${userInfo.user_id}`)
                  }
                } else {
                  if (userInfo.is_agree === false) {
                    navigate('/auth/login')
                    // navigate(`/auth/tos?provider=${userInfo.join_path}&user_id=${userInfo.user_id}`)
                  }
                }
              }

              // 추가
              return prevFunction()
            } catch {
              // Axios 인터셉터가 자동으로 토큰 갱신 처리
              try {
                return prevFunction()
              } catch {
                useAuthStore.getState().clearAuth()
                navigate('/auth/login')
              }
            }
          }
        } else {
          // 토큰이 없으면 로그인 페이지로
          navigate('/auth/login')
        }
      }
      asyncFunction()
    },
    [lastError, handleSendMessage, navigate]
  )

  // Intersection Observer를 사용한 무한스크롤
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    const observer = new IntersectionObserver(
      entries => {
        // 첫 페이지 로딩 중이면 무시
        if (!isInitialLoadComplete) return

        // 마지막 API 호출로부터 300ms가 지나지 않았으면 무시 (쿨다운 단축)
        const now = Date.now()
        if (now - lastFetchTime < 300) return

        if (entries[0].isIntersecting && hasMoreMessages && !isLoadingMore) {
          clearTimeout(timeoutId)
          timeoutId = setTimeout(() => {
            loadMoreMessages()
          }, 100) // 100ms 디바운스로 단축하여 더 빠른 반응
        }
      },
      {
        threshold: 0.1, // 더 낮은 threshold로 더 빠르게 감지
        rootMargin: '100px' // 100px 전에 미리 로딩 시작
      }
    )

    if (messagesTopRef.current) {
      observer.observe(messagesTopRef.current)
    }

    return () => {
      observer.disconnect()
      clearTimeout(timeoutId)
    }
  }, [hasMoreMessages, isLoadingMore, loadMoreMessages, isInitialLoadComplete, lastFetchTime])

  // 제목 편집 모달 오픈 시 현재 제목 설정
  useEffect(() => {
    if (showEditTitleModal) {
      setNewTitle(consultationDetail?.title || '')
    }
  }, [showEditTitleModal, consultationDetail?.title])

  // 키보드 입력 시 타이머 리셋
  useEffect(() => {
    // 빈 문자열이거나 로딩 중이면 무시
    if (inputValue.length === 0 || isLoading) return

    // consultationId가 없으면 무시
    if (!consultationId) return

    // 추천 응답이 이미 표시되어 있으면 아무것도 하지 않음 (추천 응답 유지, 새로운 타이머 시작 안 함)
    if (recommendedResponsesRef.current.length > 0) {
      return
    }

    // 기존 타이머가 있으면 클리어 (사용자 타이핑 감지)
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
      apiCallIdRef.current += 1 // API call ID 증가하여 진행 중인 API 무효화
    }

    // 타이머 다시 시작
    resetInactivityTimer(consultationId)
  }, [inputValue, consultationId, isLoading])

  useEffect(() => {
    // consultationId가 설정되지 않았으면 체크하지 않음
    if (!consultationId) {
      return
    }

    // 미션 등록 여부 확인
    const missionActionData = safeSessionStorage.getJSON<{
      action: string
      consultationId: number
      timestamp: number
    }>(SESSION_STORAGE_KEYS.MISSION_ACTION)

    if (missionActionData) {
      const { action, consultationId: savedId, timestamp } = missionActionData

      // 같은 채팅방 & 5분 이내인 경우만
      if (action === 'register' && savedId === consultationId && Date.now() - timestamp < 5 * 60 * 1000) {
        setShowToast('createMissionComplete')
        safeSessionStorage.remove(SESSION_STORAGE_KEYS.MISSION_ACTION) // 사용했으므로 삭제
      }
    }
  }, [consultationId, location.pathname])

  useEffect(() => {
    // 컴포넌트 언마운트 시 타이머 정리
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
        apiCallIdRef.current += 1 // API call ID 증가하여 진행 중인 API 무효화
      }
    }
  }, [])

  // 상담 완료 시 자동으로 평가 모달 표시
  useEffect(() => {
    if (isConsultationComplete && !isLoading) {
      // 메시지가 완전히 렌더링되길 기다림 (shouldScrollToBottom 완료 후)
      const timer = setTimeout(() => {
        setShowRatingModal(true)
        setIsConsultationComplete(false) // 리셋
      }, 4500) // 4.5초 대기

      return () => clearTimeout(timer)
    }
  }, [isConsultationComplete, isLoading])

  // 메시지 영역 높이/padding 조절 (웹 + React Native)
  useEffect(() => {
    const updateLayout = () => {
      if (!scrollContainerRef.current) return

      // 웹 환경: 기존 로직 (padding만 조정)
      scrollContainerRef.current.style.height = '' // height 제거 (flex-1 사용)

      if (recommendationContainerRef.current) {
        const recommendationHeight = recommendationContainerRef.current.offsetHeight
        const totalPadding = recommendedResponses.length === 0 ? 100 : recommendationHeight + 20
        scrollContainerRef.current.style.paddingBottom = `${totalPadding}px`
      } else {
        scrollContainerRef.current.style.paddingBottom = '150px'
      }
    }

    // 레이아웃 업데이트
    updateLayout()

    // 리사이즈 이벤트에도 대응
    window.addEventListener('resize', updateLayout)
    return () => window.removeEventListener('resize', updateLayout)
  }, [recommendedResponses])

  return (
    <div className='flex flex-col h-[100dvh] overflow-hidden relative'>
      <SSEViewer
        consultationId={consultationId ?? undefined}
        onConnectionChange={connected => setIsSSEConnected(connected)}
        onConnectionRefReady={ref => {
          sseConnectionRef.current = ref
        }}
      />
      {/* 상단 바 */}
      <MainTopBar
        hasBackButton={true}
        handleClickBackButton={() => navigate('/chat')}
        leftSide={
          <div className='flex items-center w-full'>
            <h1 className='text-header-title font-semibold text-amber-900 truncate max-w-[180px] sm:max-w-[240px] md:max-w-[320px]'>
              {consultationDetail?.title || '새로운 대화'}
            </h1>
            {consultationDetail && consultationDetail.status === 'active' && (
              <button
                onClick={() => setShowEditTitleModal(true)}
                className='ml-2 p-2 text-amber-700 hover:text-amber-900 rounded-full transition-colors'
              >
                <Edit2 size={16} />
              </button>
            )}
          </div>
        }
        rightSide={
          consultationDetail && consultationDetail.status === 'active' ? (
            <button
              onClick={() => setShowExitModal(true)}
              className='p-2 text-amber-700 hover:text-amber-900 bg-white/20 rounded-full transition-colors'
            >
              <LogOut size={18} />
            </button>
          ) : undefined
        }
      />

      {/* 메시지 영역 */}
      <main ref={scrollContainerRef} className='flex-1 overflow-y-auto px-3 py-4'>
        {/* 무한스크롤 트리거 포인트 및 로딩 인디케이터 */}
        <div ref={messagesTopRef} className='relative'>
          {isLoadingMore && (
            <div className='text-center py-4'>
              <div className='text-sm text-gray-500 flex items-center justify-center gap-2'>
                <Loader2 className='w-4 h-4 animate-spin' />
                <span>이전 메시지 불러오는 중...</span>
              </div>
            </div>
          )}
        </div>
        {messages.map(dateGroup => (
          <div key={dateGroup.date}>
            {/* 날짜 구분선 */}
            <div className='text-center my-3'>
              <span className='text-xs text-amber-800 bg-white/80 px-3 py-1 rounded-full font-medium'>
                {dateGroup.date.slice(0, 4)}년 {dateGroup.date.slice(4, 6)}월 {dateGroup.date.slice(6, 8)}일
              </span>
            </div>
            {/* 메시지 목록 */}
            <div className='flex flex-col space-y-4'>
              {dateGroup.messagesList.map((msg, index) => (
                <ChatMessageBubble
                  key={`${dateGroup.date}-${msg.id}-${msg.sender}-${index}`}
                  msg={msg}
                  isStreaming={msg.id === streamingMessageId && isLoading && msg.text === ''}
                  buttonState={
                    msg.sender === 'ai' &&
                    msg.text === '메시지 전송에 실패했습니다. 다시 시도해주세요.' &&
                    lastError !== null
                      ? 'retry'
                      : undefined
                  }
                  handleRetryAiResponse={() => handleRetryAiResponse('message')}
                />
              ))}
            </div>
          </div>
        ))}

        {/* 입력창과 추천 응답 표시 - 앱에서도 웹뷰에서 표시 */}
        {(!consultationDetail || consultationDetail.status === 'active') && !isConsultationComplete && (
          <div
            ref={recommendationContainerRef}
            className='fixed bottom-0 flex flex-col w-full left-0 space-y-2 pb-4 h-fit px-2 message-input-container'
            data-input-area='true'
          >
            {/* 추천 응답 영역 */}
            <div data-input-area='true'>
              <div className='flex flex-col gap-0.5 max-w-4xl mx-auto'>
                {recommendedResponses &&
                  recommendedResponses.length > 0 &&
                  recommendedResponses.map((response, index) => (
                    <button
                      key={index}
                      onClick={() => handleRecommendationClick(response)}
                      className='w-full text-left px-2 py-[5px] bg-white/70  hover:bg-gray-50 transition-colors text-sm text-gray-700 rounded-[10px]'
                    >
                      {response}
                    </button>
                  ))}
              </div>
            </div>

            {/* 입력 영역 - is_complete일 때 완전히 숨김 */}
            <div className='px-2'>
              <ChatMessageInput
                showTypeCheckOverlay={false}
                textareaRef={textareaRef as React.RefObject<HTMLTextAreaElement>}
                message={inputValue}
                setMessage={setInputValue}
                handleKeyPress={handleKeyPress}
                handleSendMessage={() => handleSendMessage(inputValue)}
                isSendButtonActive={inputValue.trim() !== '' && !isLoading}
                placeholderText={''}
                buttonState={isLoading ? 'stop' : 'send'}
              />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </main>

      {/* 채팅 종료 모달 */}
      {showExitModal && (
        <ChatExitModal
          showExitModal={showExitModal}
          handleClose={() => setShowExitModal(false)}
          handleConfirmExit={handleConfirmExit}
          selectedReason={selectedReason}
          setSelectedReason={setSelectedReason}
          // customReason={customReason}
          // setCustomReason={setCustomReason}
          reasons={EXIT_REASONS}
          hasReason={consultationDetail?.type === '주제상담'}
          isLoading={finishConsultationMutation.isPending}
        />
      )}

      {/* 평가 모달 */}
      {showRatingModal && (
        <ChatRateExitModal
          showRatingModal={showRatingModal}
          handleClose={() => {
            setShowRatingModal(false)
            navigate('/chat')
          }}
          handleConfirmExit={handleConfirmFeedback}
          rating={rating}
          setRating={setRating}
          handleBottomBottonClick={handleSkipFeedback}
        />
      )}

      {/* 제목 편집 모달 */}
      {showEditTitleModal && (
        <ChatEditTitleModal
          chatTitle={consultationDetail?.title || ''}
          handleClose={() => setShowEditTitleModal(false)}
          handleSaveTitle={handleSaveTitle}
          newTitle={newTitle}
          setNewTitle={setNewTitle}
        />
      )}

      {/* 토스트 메시지 */}
      {showToast && (
        <ToastMessage message={TOAST_MESSAGE[showToast]} isVisible={!!showToast} onClose={() => setShowToast(null)} />
      )}
    </div>
  )
}

export default function NonStreamingChatRoom() {
  return <NonStreamingChatRoomContent />
}
